import type { CSSProperties } from 'react'

type MotionStyle = CSSProperties & Record<`--dotit-motion-${string}`, string>

function stringValue(
  style: Record<string, unknown> | undefined,
  key: string,
  fallback: string
) {
  return typeof style?.[key] === 'string' ? style[key] : fallback
}

function numberValue(
  style: Record<string, unknown> | undefined,
  key: string,
  fallback: number
) {
  return typeof style?.[key] === 'number' ? style[key] : fallback
}

export function imageMotionClass(style: Record<string, unknown> | undefined) {
  const effect = stringValue(style, 'imageMotion', 'NONE')
  return effect === 'NONE'
    ? ''
    : `dotit-image-motion dotit-image-motion-${effect.toLowerCase().replaceAll('_', '-')}`
}

export function imageMotionStyle(
  style: Record<string, unknown> | undefined
): MotionStyle {
  const intensity = Math.min(
    10,
    Math.max(1, numberValue(style, 'motionIntensity', 5))
  )
  const duration = Math.min(
    60_000,
    Math.max(2_000, numberValue(style, 'motionDurationMs', 12_000))
  )
  const direction = stringValue(style, 'motionDirection', 'LEFT_RIGHT')
  const distance = 0.6 + intensity * 0.45
  const vectors = {
    LEFT_RIGHT: [-distance, 0, distance, 0],
    RIGHT_LEFT: [distance, 0, -distance, 0],
    UP_DOWN: [0, -distance, 0, distance],
    DOWN_UP: [0, distance, 0, -distance],
  }[direction] ?? [-distance, 0, distance, 0]
  const origin =
    {
      CENTER: '50% 50%',
      LEFT: '20% 50%',
      RIGHT: '80% 50%',
      TOP: '50% 20%',
      BOTTOM: '50% 80%',
    }[stringValue(style, 'motionFocalPoint', 'CENTER')] ?? '50% 50%'

  return {
    animationDuration: `${duration}ms`,
    animationIterationCount: style?.motionLoop === false ? '1' : 'infinite',
    animationDirection: style?.motionLoop === false ? 'normal' : 'alternate',
    animationFillMode: 'both',
    transformOrigin: origin,
    '--dotit-motion-start-x': `${vectors[0]}%`,
    '--dotit-motion-start-y': `${vectors[1]}%`,
    '--dotit-motion-end-x': `${vectors[2]}%`,
    '--dotit-motion-end-y': `${vectors[3]}%`,
    '--dotit-motion-scale': String(1 + intensity * 0.012),
    '--dotit-motion-drift-scale': String(1.02 + intensity * 0.004),
    '--dotit-motion-tilt': `${0.35 + intensity * 0.28}deg`,
    '--dotit-motion-tilt-x': `${0.12 + intensity * 0.09}deg`,
  }
}
